import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { sortBy, groupBy, flatten } from 'lodash';
import formatMoney from 'utils/formatters/Money';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';

import ChevronDownIcon from 'components/v2/icons/ChevronDownIcon';
import ChevronUpIcon from 'components/v2/icons/ChevronUpIcon';

import ToolTip from 'components/v2/Tooltip';
import ExclamationIcon from 'components/v2/icons/ExclamationIcon';

import { useTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import { Disclosure } from '@headlessui/react';
import COLUMNS from './JobListColumns';

const ACTIONS = {
  ADD: 'add',
  DELETE: 'delete',
};

const DRAYAGE = 'drayage';

const rowClass = classnames({
  grid: true,
  'grid-cols-12': true,
  'auto-cols-max': true,
  'gap-2': true,
  'p-2': true,
});

const headerClass = classnames({
  grid: true,
  'grid-cols-12': true,
  'font-medium': true,
  'p-2': true,
  'text-center': true,
  'bg-gray-50': true,
});

const renderWarningIcon = () => {
  return <ExclamationIcon className="text-red-700 w-5 h-5" />;
};

const GetButton = (action, onClick, hideButtons, lineItem) => {
  if (hideButtons) {
    return null;
  }
  if (lineItem.needs_equipment) {
    return (
      (<ToolTip dataTestId="ToolTip-6858254e" id={lineItem._id} body={renderWarningIcon}>Not applied to an equipment
              </ToolTip>)
    );
  }
  return action === ACTIONS.ADD ? (
    <AddButton dataTestId="AddButton-7f24b195" size="small" onClick={onClick} />
  ) : (
    <DeleteButton dataTestId="DeleteButton-c8ab85f4" size="small" onClick={onClick} />
  );
};

const LedgerItem = ({
  context,
  shipmentId,
  ledgerId,
  lineItem,
  update,
  onLedgerItemClick,
  hideButtons,
  index
}) => {
  let action;
  const { t } = useTranslation();
  const ledgerProperty = context === 'invoice' ? 'invoice_id' : 'payout_id';
  if (lineItem[ledgerProperty] === ledgerId) {
    if (!(update.remove || []).find((item) => item === lineItem._id)) {
      action = ACTIONS.DELETE;
    } else {
      action = ACTIONS.ADD;
    }
  }
  // TODO: Need to check for whether the invoice or payout for this line item is paid
  if (lineItem[ledgerProperty] !== ledgerId) {
    if (!(update.add || []).find((item) => item === lineItem._id)) {
      action = ACTIONS.ADD;
    } else {
      action = ACTIONS.DELETE;
    }
  }
  const onClick = (e) => onLedgerItemClick(e, action, shipmentId)(lineItem);
  const lineItemMessage =
    lineItem.type === 'customer_adjustment'
      ? titleize(lineItem.message)
      : t(sluggify(lineItem.message));
  return (
    (<div data-testid={`div-0773443d-${index}`} className="grid grid-cols-4 py-2 border-b-2">
      <p data-testid="p-a8b96c1f" />
      <p
        data-testid="p-3c3423f2"
        className="my-auto my-auto text-gray-500 break-all">
        {lineItemMessage}
      </p>
      <p
        data-testid="p-19f373d6"
        className="my-auto my-auto text-gray-500 font-semibold">
        <span
          data-testid="span-ed0b66ef"
          className={classnames({ 'text-red-500': lineItem.value < 0 })}>
          {formatMoney(lineItem.value, lineItem?.currency)}
        </span>
      </p>
      {/* {GetButton(action, onClick, hideButtons, lineItem)} */}
    </div>)
  );
};

const LedgerItemList = ({
  context,
  job,
  lineItems,
  ledgerId,
  currentUpdates,
  onLedgerItemClick,
  hideButtons,
}) => {
  const { shipment_id } = job;
  const groupingProperty = context === 'invoice' ? 'invoice_id' : 'payout_id';
  const grouped = groupBy(lineItems, groupingProperty);
  const update = (currentUpdates || {}).changes.find(
    (change) => change.shipment === job.shipment_id,
  );
  const currentLedgerLineItems = grouped[ledgerId] || [];
  const otherLedgerLineItems = flatten(
    Object.keys(grouped)
      .filter((key) => key !== ledgerId)
      .map((key) => grouped[key]),
  );

  return (<>
    {currentLedgerLineItems.map((item, index) => (<LedgerItem
      context={context}
      shipmentId={shipment_id}
      ledgerId={ledgerId}
      lineItem={item}
      update={update || {}}
      onLedgerItemClick={onLedgerItemClick}
      hideButtons={hideButtons}
      index={index}
    />))}
    {otherLedgerLineItems.map((item, index) => {
      if (!hideButtons) {
        return (
          <LedgerItem
            context={context}
            shipmentId={shipment_id}
            ledgerId={ledgerId}
            lineItem={item}
            update={update || {}}
            onLedgerItemClick={onLedgerItemClick}
            hideButtons={hideButtons}
            index={index}
          />
        );
      }
      return null;
    })}
  </>);
};

const JobRow = (ledgerType, job, currentTotal) => {
  return COLUMNS[ledgerType].map((column, index) => {
    const key = `${column.field}-${job._id}`;
    return (
      (<div
        data-testid={`div-4e0516cc-${index}`}
        key={key}
        className="mx-auto my-auto text-sm text-gray-500">
        {column.value(job, currentTotal)}
      </div>)
    );
  });
};

const JobList = ({
  context,
  ledgerId,
  ledgerType,
  version,
  jobs,
  lineItems,
  setChanges,
  onRemove,
  hideButtons,
}) => {
  const { t } = useTranslation();
  const rowExpansion = jobs?.reduce((rows, job) => {
    rows[job.shipment_id] = false;
    return rows;
  }, {});
  const [rowsExpanded, setRowsExpanded] = useState(rowExpansion);
  const [ledgerUpdates, setLedgerUpdates] = useState({ version, changes: [] });
  const [lineItemLookup, setLineItemLookup] = useState({});

  const getShipmentTotals = () => {
    return jobs?.reduce((totals, job) => {
      totals[job.shipment_id] = parseFloat(job.total);
      return totals;
    }, {});
  };
  const [currentShipmentTotals, setCurrentShipmentTotals] =
    useState(getShipmentTotals());

  const hoverLookup = () => {
    return jobs?.reduce((data, job) => {
      data[job] = false;
      return data;
    }, {});
  };
  const [isHovering, setIsHovering] = useState(hoverLookup());

  useEffect(() => {
    const rowExpansionMap = jobs?.reduce((rows, job) => {
      rows[job.shipment_id] = true;
      return rows;
    }, {});
    setRowsExpanded(rowExpansionMap);
  }, [ledgerUpdates]);

  const getLineItemLookup = (lineItems) => {
    return lineItems.reduce((data, job) => {
      const ledgerProperty = context === 'invoice' ? 'receivables' : 'payables';
      const isShorthaul = ledgerType !== DRAYAGE;
      data[job._id.toString()] = job[ledgerProperty].map((item) => {
        return { ...item, needs_equipment: !isShorthaul && !item.equipment };
      });
      return data;
    }, {});
  };

  useEffect(() => {
    const lineItemLookup = getLineItemLookup(lineItems);
    setLineItemLookup(lineItemLookup);
    setLedgerUpdates({ version, changes: [] });
  }, [lineItems]);

  useEffect(() => {
    setCurrentShipmentTotals((prevState) => {
      return jobs?.reduce((totals, job) => {
        if (!prevState || !prevState[job.shipment_id]) {
          totals[job.shipment_id] = parseFloat(job.total);
        } else {
          totals[job.shipment_id] = prevState[job.shipment_id];
        }
        return totals;
      }, {});
    });
  }, [jobs]);

  const toggleRow = (shipmentId) => {
    setRowsExpanded((prevState) => {
      return {
        ...prevState,
        [shipmentId]: !prevState[shipmentId],
      };
    });
  };

  const modifyShipmentUpdates = (action, shipment, changes, lineItem) => {
    let shipmentUpdate = changes.find((update) => update.shipment === shipment);
    if (!shipmentUpdate) {
      changes.push({ shipment, add: [], remove: [] });
      shipmentUpdate = changes.find((update) => update.shipment === shipment);
    }

    if (action === ACTIONS.ADD) {
      // Clean remove in case it was previously removed
      shipmentUpdate.remove = shipmentUpdate.remove.filter(
        (item) => item !== lineItem._id,
      );
      shipmentUpdate.add.push(lineItem._id);
    }
    if (action === ACTIONS.DELETE) {
      // Clean add in case it was previously added
      shipmentUpdate.add = shipmentUpdate.add.filter(
        (item) => item !== lineItem._id,
      );
      shipmentUpdate.remove.push(lineItem._id);
    }
    return changes;
  };

  const calculateRunningTotal = (action, shipment, lineItem) => {
    setCurrentShipmentTotals((prevState) => {
      const total =
        action === ACTIONS.ADD
          ? prevState[shipment] + parseFloat(lineItem.value)
          : prevState[shipment] - parseFloat(lineItem.value);
      return {
        ...prevState,
        [shipment]: total,
      };
    });
  };

  const onLedgerItemClick = (e, action, shipment) => (lineItem) => {
    e.stopPropagation();
    setLedgerUpdates((prevState) => {
      const { changes: prevChanges } = prevState;
      const changes = modifyShipmentUpdates(
        action,
        shipment,
        prevChanges,
        lineItem,
      );
      return {
        ...prevState,
        changes,
      };
    });
    setChanges(ledgerUpdates);
    calculateRunningTotal(action, shipment, lineItem);
  };

  const hoverClass = classnames({
    'bg-gray-50': isHovering,
  });

  const removeShipment = (e, job) => {
    e.stopPropagation();
    onRemove(job);
  };

  const setHover = (job, hoverState) => {
    setIsHovering((prevState) => {
      return {
        ...prevState,
        [job]: hoverState,
      };
    });
  };

  const disableDeleteButton = () => {
    return jobs.length <= 1;
  };

  const localizeTitles = (column) => {
    let display_text = t(column.title, {
      count: column?.count,
    });
    if (column.titleize) display_text = titleize(display_text);
    return (
      (<p
        data-testid="p-be4e0d7d"
        key={column.field}
        className="my-auto text-xs text-gray-500 uppercase">
        {display_text}
      </p>)
    );
  };

  if (!jobs || !currentShipmentTotals || !rowsExpanded) return null;
  return (
    (<div data-testid="div-7df63e75">
      <div data-testid="div-9dbef4c0" className={headerClass}>
        {COLUMNS[ledgerType]?.map((column) => localizeTitles(column))}
      </div>
      {sortBy(jobs, ['driverName', 'jobDeliverBy']).map((job, index) => {
        let className = `${rowClass} ${hoverClass}`;
        if (isHovering && job.shipmentId) {
          className = `${rowClass} ${isHovering[job.shipmentId] && hoverClass}`;
        }

        return (
          (<div
            data-testid={`div-644b2292-${index}`}
            className="pb-4"
            onMouseEnter={() => setHover(job.shipmentId, true)}
            onMouseLeave={() => setHover(job.shipmentId, false)}>
            <Disclosure>
              <div
                data-testid={`div-cc348ac4-${index}`}
                key={job._id}
                className={className}
                onClick={() => toggleRow(job.shipment_id)}>
                {JobRow(
                  ledgerType,
                  job,
                  currentShipmentTotals[job.shipment_id],
                )}{' '}
                <div data-testid={`div-c9b11934-${index}`} className="mx-auto my-auto">
                  <Disclosure.Button data-testid={`undefined-48965b74-${index}`}>
                    {({ open }) => (
                      <div
                        data-testid={`div-d2d29366-${index}`}
                        className="inline-block mt-2.5 mr-3.5">
                        {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
                      </div>
                    )}
                  </Disclosure.Button>
                  {/* {SessionStore.isAdmin() && SessionStore.isFinanceAdmin() && (
                  <DeleteButton
                    className="float-right"
                    onClick={(e) => removeShipment(e, job)}
                    disabled={disableDeleteButton}
                  />
                )} */}
                </div>
              </div>
              <Disclosure.Panel data-testid={`undefined-d698b0f6-${index}`}>
                <div data-testid={`div-1ba764db-${index}`} className="w-2/5">
                  <LedgerItemList
                    context={context}
                    job={job}
                    lineItems={lineItemLookup[job.shipment_id]}
                    ledgerId={ledgerId}
                    currentUpdates={ledgerUpdates}
                    onLedgerItemClick={onLedgerItemClick}
                    hideButtons={hideButtons}
                  />
                </div>
              </Disclosure.Panel>
            </Disclosure>
          </div>)
        );
      })}
    </div>)
  );
};

export default JobList;

JobList.propTypes = {
  ledgerType: PropTypes.string.isRequired,
  ledgerId: PropTypes.string.isRequired,
  version: PropTypes.number.isRequired,
  jobs: PropTypes.array.isRequired,
};
